import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { useTranslations } from 'gt-react'
import { ArrowLeft, Loader2, Plus } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'
import { createWorkspace, PlatformApiError, requestSsoHandoff } from '@/lib/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/account/new')({
  component: NewWorkspacePage,
})

/**
 * Self-service workspace creation. The customer fills in a name,
 * the backend provisions a new tenant DB, seeds the creator as admin,
 * and registers them in the cross-tenant user index. On success,
 * the user is SSO'd into the new workspace immediately.
 */
function NewWorkspacePage() {
  const t = useTranslations('new_workspace')
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim() || busy) return

    setBusy(true)
    setError(null)

    try {
      const { tenant } = await createWorkspace(name.trim())

      // SSO into the new workspace immediately so the user lands
      // in their fresh ERP without a second sign-in.
      try {
        const { url } = await requestSsoHandoff(tenant.id)
        window.location.href = url
      } catch {
        // SSO failed — fall back to the account page where the
        // workspace will appear in the list with an "Open" button.
        void navigate({ to: '/account' })
      }
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message
          ? err.message
          : 'Failed to create workspace. Please try again.'
      setError(message)
      setBusy(false)
    }
  }

  return (
    <>
      <NavBar />
      <main className="flex min-h-screen flex-col">
        <section className="relative flex-1 pt-32 pb-24">
          <div
            className="pointer-events-none absolute inset-0 -z-10 marketing-grid-bg opacity-20"
            aria-hidden="true"
          />
          <div className="mx-auto w-full max-w-2xl px-4 sm:px-6">
            <Link
              to="/account"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('back')}
            </Link>

            <motion.header
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6"
            >
              <h1 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
                <span className="marketing-gradient-text">{t('title')}</span>
              </h1>
              <p className="mt-2 text-pretty text-muted-foreground">{t('subtitle')}</p>
            </motion.header>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={cn(
                'relative mt-10 overflow-hidden rounded-2xl border border-border bg-card/80 backdrop-blur-md p-8',
                'shadow-xl shadow-primary/5',
              )}
            >
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label
                    htmlFor="workspace-name"
                    className="block text-sm font-medium mb-1.5"
                  >
                    Workspace name
                  </label>
                  <input
                    id="workspace-name"
                    type="text"
                    required
                    minLength={2}
                    maxLength={100}
                    // biome-ignore lint/a11y/noAutofocus: primary field of a dedicated create-workspace form
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={busy}
                    placeholder="e.g. Acme Corp"
                    className={cn(
                      'h-11 w-full rounded-xl border border-border bg-background px-4 text-sm',
                      'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
                      'disabled:opacity-60 disabled:cursor-not-allowed',
                    )}
                  />
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    This is the display name your team sees. You can rename it later from
                    workspace settings.
                  </p>
                </div>

                {error ? (
                  <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    {error}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={busy || name.trim().length < 2}
                  className={cn(
                    'inline-flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold',
                    'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                    'hover:opacity-90 transition-opacity',
                    'disabled:opacity-60 disabled:cursor-not-allowed',
                  )}
                >
                  {busy ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating workspace...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Create workspace
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
